// src/components/layout/AlterarSenha/AlterarSenhaModal.js
//
// Modal de troca de senha — compartilhado entre UserPage.js (dropdown do
// professor) e PaginaUsuario.js (página do ADM). Pede a senha ATUAL antes
// de trocar (reautentica com EmailAuthProvider) em vez de chamar
// updatePassword direto, por dois motivos:
//   1. Segurança: qualquer um com a aba aberta (sessão já logada) não
//      consegue trocar a senha sem saber a atual.
//   2. O Firebase Auth exige login "recente" pra updatePassword — sem
//      reautenticar, sessões mais antigas caem em auth/requires-recent-login.
//
// Usa `getAuth().currentUser` como fonte do e-mail (não o campo do
// Firestore) porque professores não têm `email` gravado no doc
// usuarios/{uid} hoje — currentUser.email do Auth sempre existe.

import { useState } from 'react'
import { getAuth, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth'
import { X } from 'lucide-react'
import Input from '../Input/Input'
import Button from '../Button/Button'
import { useToast } from '../../../hooks/Toast'
import style from './alterarSenha.module.css'

export default function AlterarSenhaModal({ aberto, onFechar }) {
  const { mostrarAviso } = useToast()
  const [senhaAtual, setSenhaAtual] = useState('')
  const [novaSenha, setNovaSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [enviando, setEnviando] = useState(false)

  if (!aberto) return null

  const limparEFechar = () => {
    setSenhaAtual('')
    setNovaSenha('')
    setConfirmarSenha('')
    onFechar()
  }

  const salvar = async (e) => {
    e.preventDefault()

    if (novaSenha.length < 6) {
      mostrarAviso('A nova senha precisa ter pelo menos 6 caracteres', 'aviso')
      return
    }
    if (novaSenha !== confirmarSenha) {
      mostrarAviso('A confirmação não bate com a nova senha', 'aviso')
      return
    }

    const usuarioAuth = getAuth().currentUser
    if (!usuarioAuth) {
      mostrarAviso('Sessão expirada. Faça login novamente.', 'erro')
      return
    }

    setEnviando(true)
    try {
      const credencial = EmailAuthProvider.credential(usuarioAuth.email, senhaAtual)
      await reauthenticateWithCredential(usuarioAuth, credencial)
      await updatePassword(usuarioAuth, novaSenha)
      mostrarAviso('Senha alterada com sucesso', 'sucesso')
      limparEFechar()
    } catch (error) {
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        mostrarAviso('Senha atual incorreta', 'erro')
      } else if (error.code === 'auth/too-many-requests') {
        mostrarAviso('Muitas tentativas. Aguarde um pouco e tente de novo.', 'erro')
      } else {
        mostrarAviso('Erro ao trocar a senha: ' + error.message, 'erro')
      }
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className={style.overlay} onClick={limparEFechar}>
      <form className={style.modal} onClick={(e) => e.stopPropagation()} onSubmit={salvar}>
        <div className={style.cabecalho}>
          <h3>Alterar senha</h3>
          <X size={20} onClick={limparEFechar} className={style.fechar} />
        </div>

        <Input
          label="Senha atual"
          type="password"
          name="senhaAtual"
          value={senhaAtual}
          onChange={(e) => setSenhaAtual(e.target.value)}
          required
          placeholder="••••••••"
        />
        <Input
          label="Nova senha"
          type="password"
          name="novaSenha"
          value={novaSenha}
          onChange={(e) => setNovaSenha(e.target.value)}
          required
          minLength={6}
          placeholder="Mínimo 6 caracteres"
        />
        <Input
          label="Confirmar nova senha"
          type="password"
          name="confirmarSenha"
          value={confirmarSenha}
          onChange={(e) => setConfirmarSenha(e.target.value)}
          required
          minLength={6}
          placeholder="Repita a nova senha"
        />

        <div className={style.botoes}>
          <Button type="button" variant="outline" onClick={limparEFechar}>Cancelar</Button>
          <Button type="submit" disabled={enviando}>{enviando ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </form>
    </div>
  )
}
